import { useEffect, useMemo } from "react";
import { FieldValues, Path, useFormContext } from "react-hook-form";
import { DrivingLicenseClass } from "../model/DrivingLicenseClass";
import { SelectInput } from "./SelectInput";
import { Student } from "../providers/studentsProvider";

export function DrivingLicenseClassInput(props: {
  source: Path<FieldValues>;
  bulkEdit?: boolean;
  student: Student;
  label: string;
  includesSimulatorDrivingLessons?: boolean;
  showFinishedBookedTrainings?: boolean;
  excludeCancelledBookedTrainings?: boolean;
  size?: "small";
}) {
  const {
    source,
    bulkEdit,
    student,
    label,
    includesSimulatorDrivingLessons,
    showFinishedBookedTrainings,
    excludeCancelledBookedTrainings,
    size,
  } = props;
  const { getValues, setValue } = useFormContext();
  const defaultValue = student.activeOrMostRecentBookedTraining.isForDrivingLicense
    ? student.activeOrMostRecentBookedTraining.drivingLicenseClass
    : "";
  const options: Array<[DrivingLicenseClass, string]> = useMemo(() => {
    let { bookedTrainings } = student;
    if (!showFinishedBookedTrainings) {
      bookedTrainings = bookedTrainings.filter((it) => !it.isFinished);
    }
    if (excludeCancelledBookedTrainings) {
      bookedTrainings = bookedTrainings.filter((it) => !it.isCancelled);
    }
    if (includesSimulatorDrivingLessons) {
      bookedTrainings = bookedTrainings.filter((it) => it.includesSimulatorDrivingLessons);
    }
    if (bookedTrainings.length === 0) {
      console.warn(`Did not find relevant booked training for student ${student.id} (${student.name})`);
    }
    const result = bookedTrainings
      .map((it) => {
        let suffix = "";
        if (it.isCancelled) {
          suffix = " (abgebrochen)";
        } else if (it.isFinished) {
          suffix = " (abgeschlossen)";
        }
        const labelWithStatus = `${it.drivingLicenseClass}${suffix}`;
        return [it.drivingLicenseClass, labelWithStatus];
      })
      .sort(([classA, _labelA], [classB, _labelB]) => classA.localeCompare(classB));
    return result as Array<[DrivingLicenseClass, string]>;
  }, [student, excludeCancelledBookedTrainings, includesSimulatorDrivingLessons, showFinishedBookedTrainings]);

  useEffect(() => {
    const selection = getValues(source);
    if (bulkEdit && selection === "__DO_NOT_CHANGE__") {
      return;
    }
    if (options.some(([value]) => value === selection)) {
      return;
    }
    setValue(source, options.some(([value]) => value === defaultValue) ? defaultValue : "");
  }, [options, source, bulkEdit, defaultValue]);

  if (options.length < 1) {
    return null;
  } else {
    return (
      <SelectInput
        source={source}
        bulkEdit={bulkEdit}
        label={label}
        options={options}
        validate={(value) => (value ? true : "Bitte wähle die Führerscheinklasse aus.")}
        size={size}
        defaultValue={defaultValue}
      />
    );
  }
}
